import { TestStatus } from "@/lib/constant";
import { shuffleArray } from "@/lib/utils";

/** Supabase `exams.status`(string)을 앱 `TestStatus`로 정규화한다. 알 수 없는 값은 pending. */
export function parseExamStatus(value: string): TestStatus {
  switch (value) {
    case TestStatus.COMPLETED:
    case TestStatus.IN_PROGRESS:
    case TestStatus.PENDING:
      return value;
    default:
      return TestStatus.PENDING;
  }
}

export type GeneratedOption = {
  options: [string, string, string, string];
  correctOption: 1 | 2 | 3 | 4;
};

/**
 * 단어의 정답과 오답 후보에서 보기 4개를 생성한다.
 *
 * 규칙:
 * 1. 정답(correctMeaning)은 반드시 보기에 포함
 * 2. 오답 3개는 정답과 다른 meaning (서로도 달라야 함)
 * 3. 정답 위치는 랜덤
 *
 * PL/pgSQL generate_exam 함수와 동일한 로직의 TypeScript 구현.
 * 프론트 프리뷰 및 단위 테스트용.
 */
export function generateOptions(correctMeaning: string, candidateMeanings: string[]): GeneratedOption {
  // 정답과 다른 + 중복 없는 meaning만 필터
  const uniqueWrong = [...new Set(candidateMeanings)].filter(m => m !== correctMeaning);

  if (uniqueWrong.length < 3) {
    throw new Error(`Not enough unique wrong options: need 3, got ${uniqueWrong.length}`);
  }

  // 랜덤 3개 추출
  const shuffled = shuffleArray(uniqueWrong);
  const wrongOptions = shuffled.slice(0, 3);

  // 정답 + 오답 3개를 합쳐서 셔플
  const allOptions = [correctMeaning, ...wrongOptions];
  const finalOptions = shuffleArray(allOptions);

  // 정답 위치 찾기 (1-indexed)
  const correctOption = (finalOptions.indexOf(correctMeaning) + 1) as 1 | 2 | 3 | 4;

  return {
    options: finalOptions as [string, string, string, string],
    correctOption,
  };
}

/**
 * 가입일 기준으로 오늘까지의 날짜 목록 생성
 */
export function generateDateRange(createdAt: string): string[] {
  const dates: string[] = [];
  const start = new Date(createdAt);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const current = new Date(start);
  while (current <= today) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }

  return dates;
}
